import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./ui/select";
import { X } from "lucide-react";

const WHATSAPP_NUMBER = "919926159666";

const BROCHURE_URL =
  "https://customer-assets.emergentagent.com/job_villa-awaits/artifacts/ze2k80un_Dream%2BHomes%2B2.pdf";

const SELLDO_CONFIG = {
  direct: String(process.env.REACT_APP_SELLDO_DIRECT || "").toLowerCase() === "true",
  apiKey: process.env.REACT_APP_SELLDO_API_KEY || "",
  srd: process.env.REACT_APP_SELLDO_SRD || "generic",
  endpoint:
    process.env.REACT_APP_SELLDO_ENDPOINT ||
    "https://app.sell.do/api/leads/create",
};

export function LeadFormModal({
  isOpen,
  onClose,
  formType = "brochure",
  title = "Get Project Details"
}) {
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    budget_range: "",
    move_in_timeline: "",
    preferred_date: "",
    preferred_time: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.full_name || !formData.phone) {
      setError("Please fill in all required fields");
      return;
    }

    if (!/^[0-9]{10}$/.test(formData.phone)) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsSubmitting(true);

    // If requested, push directly to Sell.do from the browser using a hidden form
    if (SELLDO_CONFIG.direct && SELLDO_CONFIG.apiKey) {
      try {
        const formEl = document.createElement("form");
        formEl.method = "POST";
        formEl.action = SELLDO_CONFIG.endpoint;

        // Send in hidden iframe to avoid navigating away
        const iframe = document.createElement("iframe");
        iframe.name = "selldo_iframe";
        iframe.style.display = "none";
        document.body.appendChild(iframe);
        formEl.target = "selldo_iframe";

        const add = (k, v) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = k;
          input.value = v ?? "";
          formEl.appendChild(input);
        };

        // Keys (Sell.do supports various aliases; include common ones)
        add("form_id", SELLDO_CONFIG.apiKey);
        add("access_key", SELLDO_CONFIG.apiKey);
        add("api_key", SELLDO_CONFIG.apiKey);

        add("sell_do[campaign][srd]", SELLDO_CONFIG.srd);
        add("sell_do[form][lead][name]", formData.full_name);
        add(
          "sell_do[form][lead][phone]",
          formData.phone.startsWith("+") ? formData.phone : `+91${formData.phone}`
        );
        add("sell_do[form][lead][email]", "");

        // Optional details
        if (formType === "brochure" && formData.budget_range) {
          add("sell_do[form][custom][budget_range]", formData.budget_range);
        }
        if (formData.move_in_timeline) {
          add("sell_do[form][custom][move_in_timeline]", formData.move_in_timeline);
        }

        // Note/context
        let note = "Website Lead";
        if (formType === "brochure") note = "Brochure Download";
        else if (formType === "site_visit") note = `Site Visit | ${formData.preferred_date || "Flexible"} | ${formData.preferred_time || "Flexible"}`;
        else if (formType === "availability") note = "Availability Check";
        else if (formType === "floor_plan") note = "Floor Plan Request";
        add("sell_do[form][note][content]", note);

        document.body.appendChild(formEl);
        formEl.submit();

        // Cleanup iframe and form after a short delay
        setTimeout(() => {
          try {
            document.body.removeChild(formEl);
            document.body.removeChild(iframe);
          } catch {}
        }, 2000);
      } catch (err) {
        console.error("Sell.do direct push error:", err);
      }
    } else {
      // Fallback: use backend API (recommended for security)
      try {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1");
      const apiBase =
        process.env.REACT_APP_API_BASE || (isLocal ? "http://127.0.0.1:8000/api" : "/api");

      if (formType === "brochure") {
        await fetch(`${apiBase}/leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            full_name: formData.full_name,
            phone: formData.phone,
            budget_range: formData.budget_range || null,
            move_in_timeline: formData.move_in_timeline || null,
            source: "website",
            lead_type: "brochure_download"
          })
        });
      } else if (formType === "site_visit") {
        await fetch(`${apiBase}/site-visits`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            full_name: formData.full_name,
            phone: formData.phone,
            preferred_date: formData.preferred_date || null,
            preferred_time: formData.preferred_time || null
          })
        });
      } else if (formType === "availability" || formType === "floor_plan") {
        await fetch(`${apiBase}/inquiries`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.full_name,
            phone: formData.phone,
            message:
              formType === "availability"
                ? "Availability check from website"
                : "Floor plan request from website",
            inquiry_type: formType
          })
        });
      }
      } catch (err) {
        console.error("API error:", err);
      }
    }

    if (formType === "brochure") {
      window.open(BROCHURE_URL, "_blank");

      const message = encodeURIComponent(
        `Hi! I'm ${formData.full_name}. I downloaded the Dream Homes 2 brochure. Budget: ${
          formData.budget_range || "Not specified"
        }. Timeline: ${formData.move_in_timeline || "Not specified"}.`
      );

      setTimeout(() => {
        window.open(
          `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`,
          "_blank"
        );
      }, 1000);
    }

    if (formType === "site_visit") {
      const message = encodeURIComponent(
        `Hi! I'm ${formData.full_name}. I want to schedule a site visit for Dream Homes 2. Date: ${
          formData.preferred_date || "Flexible"
        }, Time: ${formData.preferred_time || "Flexible"}`
      );

      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
    }

    if (formType === "availability") {
      const message = encodeURIComponent(
        `Hi! I'm ${formData.full_name}. Please share available units in Dream Homes 2.`
      );

      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
    }

    if (formType === "floor_plan") {
      const message = encodeURIComponent(
        `Hi! I'm ${formData.full_name}. Please send the Dream Homes 2 floor plan.`
      );

      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
    }

    setFormData({
      full_name: "",
      phone: "",
      budget_range: "",
      move_in_timeline: "",
      preferred_date: "",
      preferred_time: ""
    });

    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            {title}
          </DialogTitle>

          <button
            onClick={onClose}
            className="absolute right-4 top-4"
          >
            <X size={20} />
          </button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          <div>
            <Label>Full Name *</Label>
            <Input
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Enter your name"
              required
            />
          </div>

          <div>
            <Label>Phone Number *</Label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit number"
              required
            />
          </div>

          {formType === "brochure" && (
            <>
              <Label>Budget Range</Label>
              <Select
                onValueChange={(value) =>
                  handleSelectChange("budget_range", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Budget" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30-40L">30-40 Lakhs</SelectItem>
                  <SelectItem value="40-50L">40-50 Lakhs</SelectItem>
                  <SelectItem value="50-60L">50-60 Lakhs</SelectItem>
                  <SelectItem value="60L+">60+ Lakhs</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-black text-white py-3 rounded-lg"
          >
            {isSubmitting
              ? "Submitting..."
              : formType === "brochure"
              ? "Download Brochure"
              : "Submit"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default LeadFormModal;
